import React, { useState, useEffect, useMemo, useRef } from "react";
import { Icon } from "@iconify/react";
import api from "../services/api";
import { showToast } from "../helper/notifications";
import { Link } from "react-router-dom";
import { exportToGoogleSheets } from "../utils/exportToGoogleSheets";

// Master column registry definitions
const ALL_COLUMNS = [
  // Identity & Core
  { key: "location_name", label: "Location Name", category: "General", type: "text", width: 220, required: true, defaultVisible: true },
  { key: "store_code", label: "Store Code", category: "General", type: "text", width: 140, required: true, defaultVisible: true },
  { key: "top_location_id", label: "Top Location", category: "General", type: "select", width: 180, defaultVisible: true },
  { key: "internal_id", label: "Internal ID", category: "General", type: "text", width: 140, defaultVisible: false },
  
  // Address & Geo
  { key: "street_address", label: "Street", category: "Address", type: "text", width: 180, defaultVisible: true },
  { key: "house_number", label: "House No.", category: "Address", type: "text", width: 100, defaultVisible: true },
  { key: "postal_code", label: "Postal Code", category: "Address", type: "text", width: 120, defaultVisible: true },
  { key: "city", label: "City", category: "Address", type: "text", width: 150, defaultVisible: true },
  { key: "country", label: "Country", category: "Address", type: "text", width: 120, defaultVisible: true },
  { key: "lat", label: "Latitude", category: "Address", type: "number", width: 130, defaultVisible: true },
  { key: "lng", label: "Longitude", category: "Address", type: "number", width: 130, defaultVisible: true },

  // Contact & Links
  { key: "phone_number", label: "Phone", category: "Contact", type: "text", width: 160, defaultVisible: true },
  { key: "website_url", label: "Website", category: "Contact", type: "text", width: 200, defaultVisible: true },
  { key: "google_place_id", label: "Google Place ID", category: "Contact", type: "text", width: 200, defaultVisible: false },
  { key: "google_link", label: "Google Link", category: "Contact", type: "text", width: 220, defaultVisible: false },
  { key: "description", label: "Description", category: "General", type: "textarea", width: 250, defaultVisible: false },

  // Publishing Permissions
  { key: "publish_google_business", label: "Publish GBP", category: "Publishing", type: "boolean", width: 130, defaultVisible: true },
  { key: "publish_apple_business", label: "Publish Apple", category: "Publishing", type: "boolean", width: 130, defaultVisible: true },

  // Parking Flags
  { key: "on_site_parking", label: "On-site Parking", category: "Parking", type: "boolean", width: 130, defaultVisible: false },
  { key: "free_parking_lot", label: "Free Parking Lot", category: "Parking", type: "boolean", width: 140, defaultVisible: false },
  { key: "paid_parking_lot", label: "Paid Parking Lot", category: "Parking", type: "boolean", width: 140, defaultVisible: false },
  { key: "paid_street_parking", label: "Paid Street Parking", category: "Parking", type: "boolean", width: 150, defaultVisible: false },
  { key: "free_multi_storey_car_park", label: "Free Garage", category: "Parking", type: "boolean", width: 130, defaultVisible: false },
  { key: "paid_multi_storey_car_park", label: "Paid Garage", category: "Parking", type: "boolean", width: 130, defaultVisible: false },
  { key: "free_of_charge_street_parking", label: "Free Street Parking", category: "Parking", type: "boolean", width: 150, defaultVisible: false },

  // Accessibility
  { key: "wheelchair_accessible_entrance", label: "Wheelchair Entrance", category: "Accessibility", type: "boolean", width: 160, defaultVisible: false },
  { key: "wheelchair_accessible_car_park", label: "Wheelchair Parking", category: "Accessibility", type: "boolean", width: 150, defaultVisible: false },
  { key: "wheelchair_accessible_seating", label: "Wheelchair Seating", category: "Accessibility", type: "boolean", width: 150, defaultVisible: false },
  { key: "wheelchair_accessible_toilet", label: "Wheelchair Toilet", category: "Accessibility", type: "boolean", width: 150, defaultVisible: false },
  { key: "assistive_hearing_loop", label: "Hearing Loop", category: "Accessibility", type: "boolean", width: 140, defaultVisible: false },
  { key: "appointment_required", label: "Appointment Req.", category: "Accessibility", type: "boolean", width: 145, defaultVisible: false },

  // Inclusivity
  { key: "gender_neutral_toilets", label: "Gender Neutral WC", category: "Inclusivity", type: "boolean", width: 150, defaultVisible: false },
  { key: "transgender_safe_space", label: "Trans Safe Space", category: "Inclusivity", type: "boolean", width: 150, defaultVisible: false },
  { key: "lgbtq_friendly", label: "LGBTQ+ Friendly", category: "Inclusivity", type: "boolean", width: 140, defaultVisible: false },

  // Metadata (Read-Only)
  { key: "version_number", label: "Ver #", category: "System", type: "readonly", width: 90, defaultVisible: true },
  { key: "updated_at", label: "Last Modified", category: "System", type: "datetime", width: 160, defaultVisible: true },
];

const STORAGE_KEY_COLUMNS = "loqation_master_sheet_columns_v1";

const LocationSpreadsheetView = ({
  data = [],
  loading = false,
  onRefresh,
  activeAreaId,
  onOpenHistory,
  onOpenCompare,
}) => {
  // 1. Column Config & Ordering
  const [columnsOrder, setColumnsOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_COLUMNS);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const savedKeys = new Set(parsed.map((c) => c.key));
          const missing = ALL_COLUMNS.filter((c) => !savedKeys.has(c.key)).map((c) => ({
            key: c.key,
            visible: c.defaultVisible,
          }));
          return [...parsed, ...missing];
        }
      }
    } catch (e) {
      console.error("Failed to parse column preferences:", e);
    }
    return ALL_COLUMNS.map((c) => ({ key: c.key, visible: c.defaultVisible }));
  });

  // Top locations for dropdown lookup
  const [topLocations, setTopLocations] = useState([]);

  // Local grid edits: { [location_id]: { [field]: value } }
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [columnSearch, setColumnSearch] = useState("");
  const [isColumnDropdownOpen, setIsColumnDropdownOpen] = useState(false);
  const [activeFilterText, setActiveFilterText] = useState("");

  const dropdownRef = useRef(null);

  // Save column order to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_COLUMNS, JSON.stringify(columnsOrder));
    } catch (e) {
      console.error("Failed to store column preferences:", e);
    }
  }, [columnsOrder]);

  // Fetch top locations for dropdown
  useEffect(() => {
    const fetchTopLocations = async () => {
      try {
        const res = await api.get("/top-locations", { params: { limit: 500 } });
        if (res.data?.data) {
          setTopLocations(res.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch top locations list:", err);
      }
    };
    fetchTopLocations();
  }, []);

  // Close column dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsColumnDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard shortcut: Ctrl+S / Cmd+S to save changes
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        handleSaveAll();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [edits]);

  // Map of column details
  const colMap = useMemo(() => {
    const m = new Map();
    ALL_COLUMNS.forEach((c) => m.set(c.key, c));
    return m;
  }, []);

  // Visible columns in user's custom sequence
  const visibleColumns = useMemo(() => {
    return columnsOrder
      .filter((c) => c.visible)
      .map((c) => colMap.get(c.key))
      .filter(Boolean);
  }, [columnsOrder, colMap]);

  // Count of pending edits
  const pendingCount = useMemo(() => {
    return Object.keys(edits).reduce((sum, locId) => {
      const locEdits = edits[locId] || {};
      return sum + Object.keys(locEdits).length;
    }, 0);
  }, [edits]);

  const editedRowsCount = useMemo(() => Object.keys(edits).length, [edits]);

  // Helper to suggest the next available store_code
  const suggestNextStoreCodeClient = (baseCode, usedUpperSet) => {
    const trimmed = String(baseCode || "").trim();
    if (!trimmed) return "";

    const match = trimmed.match(/^(.*?)(\d+)$/);
    if (match) {
      const prefix = match[1];
      const numStr = match[2];
      const width = numStr.length;
      let num = parseInt(numStr, 10);
      for (let i = 0; i < 10000; i++) {
        num += 1;
        const candidate = `${prefix}${String(num).padStart(width, "0")}`;
        if (!usedUpperSet.has(candidate.toUpperCase())) {
          return candidate;
        }
      }
    }

    for (let i = 1; i < 10000; i++) {
      const suffix = String(i).padStart(2, "0");
      const candidate = `${trimmed}-${suffix}`;
      if (!usedUpperSet.has(candidate.toUpperCase())) {
        return candidate;
      }
    }
    return `${trimmed}-${Date.now().toString().slice(-4)}`;
  };

  // Detect duplicate store_code values across all locations + pending edits
  const storeCodeConflicts = useMemo(() => {
    const conflicts = {};
    const allRows = Array.isArray(data) ? data : [];

    // Collect effective store_code per location
    const effectiveByLocId = new Map();
    const usedUpperSet = new Set();

    allRows.forEach((row) => {
      const locId = String(row.location_id);
      const hasEdit =
        edits[row.location_id] &&
        Object.prototype.hasOwnProperty.call(edits[row.location_id], "store_code");
      const val = hasEdit ? edits[row.location_id].store_code : row.store_code;
      const trimmed = String(val || "").trim();
      effectiveByLocId.set(locId, {
        code: trimmed,
        upper: trimmed.toUpperCase(),
        location_name: row.location_name || locId,
        isEdited: Boolean(hasEdit),
      });
      if (trimmed) {
        usedUpperSet.add(trimmed.toUpperCase());
      }
    });

    // Check edited rows for collisions with any other row
    effectiveByLocId.forEach((entry, locId) => {
      if (!entry.isEdited || !entry.code) return;

      for (const [otherLocId, otherEntry] of effectiveByLocId.entries()) {
        if (otherLocId === locId || !otherEntry.code) continue;
        if (otherEntry.upper === entry.upper) {
          const suggestion = suggestNextStoreCodeClient(entry.code, usedUpperSet);
          conflicts[locId] = {
            storeCode: entry.code,
            conflictingLocation: {
              location_id: otherLocId,
              location_name: otherEntry.location_name,
            },
            suggestion,
          };
          break;
        }
      }
    });

    return conflicts;
  }, [data, edits]);

  // Get current effective value for a cell
  const getCellValue = (row, colKey) => {
    if (edits[row.location_id] && edits[row.location_id].hasOwnProperty(colKey)) {
      return edits[row.location_id][colKey];
    }
    return row[colKey];
  };

  // Is cell edited?
  const isCellEdited = (locId, colKey) => {
    return edits[locId] && edits[locId].hasOwnProperty(colKey);
  };

  // Handle cell edit change
  const handleCellChange = (locId, colKey, value) => {
    setEdits((prev) => {
      const rowEdits = { ...(prev[locId] || {}) };
      
      const origRow = data.find((r) => r.location_id === locId);
      const origVal = origRow ? origRow[colKey] : undefined;

      const normOrig = origVal == null ? "" : String(origVal);
      const normNew = value == null ? "" : String(value);

      if (normOrig === normNew) {
        delete rowEdits[colKey];
      } else {
        rowEdits[colKey] = value;
      }

      if (Object.keys(rowEdits).length === 0) {
        const next = { ...prev };
        delete next[locId];
        return next;
      }

      return {
        ...prev,
        [locId]: rowEdits,
      };
    });

    if (autoSave) {
      triggerSingleRowSave(locId, colKey, value);
    }
  };

  const triggerSingleRowSave = async (locId, colKey, value) => {
    if (colKey === "store_code") {
      const trimmed = String(value || "").trim();
      if (trimmed) {
        const conflictRow = (Array.isArray(data) ? data : []).find(
          (r) =>
            String(r.location_id) !== String(locId) &&
            String(getCellValue(r, "store_code") || "").trim().toUpperCase() ===
              trimmed.toUpperCase()
        );
        if (conflictRow) {
          const usedSet = new Set(
            (Array.isArray(data) ? data : [])
              .map((r) => String(getCellValue(r, "store_code") || "").trim().toUpperCase())
              .filter(Boolean)
          );
          const suggestion = suggestNextStoreCodeClient(trimmed, usedSet);
          showToast(
            "error",
            `Store Code "${trimmed}" already exists (${conflictRow.location_name || conflictRow.location_id}). Suggested: ${suggestion}`
          );
          return;
        }
      }
    }

    try {
      const payload = { location_id: locId, [colKey]: value };
      const res = await api.post("/main-locations/batch-update", { locations: [payload] });
      if (res.data?.errors?.length > 0) {
        const firstErr = res.data.errors[0];
        showToast(
          "error",
          firstErr.suggestion
            ? `${firstErr.error} Suggested: ${firstErr.suggestion}`
            : firstErr.error || "Failed to save field."
        );
        return;
      }
      showToast("success", `Saved ${colKey}`);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Auto-save failed:", err);
      const errMsg = err?.response?.data?.error || "Failed to auto-save field.";
      showToast("error", errMsg);
    }
  };

  // Save all modified rows in batch
  const handleSaveAll = async () => {
    const locIds = Object.keys(edits);
    if (locIds.length === 0) {
      showToast("info", "No unsaved changes.");
      return;
    }

    const conflictIds = Object.keys(storeCodeConflicts);
    if (conflictIds.length > 0) {
      const firstConflict = storeCodeConflicts[conflictIds[0]];
      showToast(
        "error",
        `Duplicate Store Code "${firstConflict.storeCode}" (already used by ${firstConflict.conflictingLocation?.location_name}). Try "${firstConflict.suggestion}".`
      );
      return;
    }

    setSaving(true);
    try {
      const locationsPayload = locIds.map((locId) => ({
        location_id: locId,
        ...edits[locId],
      }));

      const res = await api.post("/main-locations/batch-update", {
        locations: locationsPayload,
      });

      if (res.data?.errors?.length > 0) {
        const firstErr = res.data.errors[0];
        showToast(
          "error",
          firstErr.suggestion
            ? `${firstErr.error} Suggested: ${firstErr.suggestion}`
            : firstErr.error || "Some rows failed to save."
        );
        if ((res.data?.results?.length || 0) > 0 && onRefresh) {
          await onRefresh();
        }
        return;
      }

      if (res.data?.success) {
        showToast("success", `Saved changes for ${locIds.length} location(s)!`);
        setEdits({});
        if (onRefresh) await onRefresh();
      }
    } catch (err) {
      console.error("Batch save error:", err);
      showToast("error", err?.response?.data?.error || "Failed to save batch edits.");
    } finally {
      setSaving(false);
    }
  };

  // Discard all edits
  const handleDiscardAll = () => {
    if (pendingCount > 0) {
      if (window.confirm(`Discard all ${pendingCount} pending changes across ${editedRowsCount} location(s)?`)) {
        setEdits({});
        showToast("info", "All pending edits discarded.");
      }
    }
  };

  // Column Reordering: Move column Up or Down in sequence
  const handleMoveColumn = (index, direction, e) => {
    e.stopPropagation();
    const newOrder = [...columnsOrder];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;

    const [moved] = newOrder.splice(index, 1);
    newOrder.splice(targetIndex, 0, moved);
    setColumnsOrder(newOrder);
  };

  // Toggle single column visibility
  const handleToggleColumn = (key) => {
    setColumnsOrder((prev) =>
      prev.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c))
    );
  };

  // Select all / Deselect all
  const handleSelectAllColumns = (val) => {
    setColumnsOrder((prev) => prev.map((c) => ({ ...c, visible: val })));
  };

  // Reset columns to defaults
  const handleResetColumns = () => {
    setColumnsOrder(ALL_COLUMNS.map((c) => ({ key: c.key, visible: c.defaultVisible })));
    showToast("info", "Columns reset to default view.");
  };

  // Export visible data to CSV
  const handleExportCSV = () => {
    if (!data || data.length === 0) {
      showToast("info", "No data to export.");
      return;
    }

    const headers = visibleColumns.map((c) => `"${c.label.replace(/"/g, '""')}"`).join(",");
    const rows = filteredData.map((row) => {
      return visibleColumns
        .map((c) => {
          const val = getCellValue(row, c.key);
          if (val == null) return '""';
          if (c.type === "boolean") return val ? '"Yes"' : '"No"';
          if (c.key === "top_location_id") {
            const tl = topLocations.find((t) => t.id === val);
            return `"${(tl?.name || row.top_location_name || val || "").replace(/"/g, '""')}"`;
          }
          return `"${String(val).replace(/"/g, '""')}"`;
        })
        .join(",");
    });

    const csvContent = "data:text/csv;charset=utf-8," + [headers, ...rows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Master_Locations_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [exportingSheets, setExportingSheets] = useState(false);

  // Export visible data to Google Sheets
  const handleExportGoogleSheets = async () => {
    if (!data || data.length === 0) {
      showToast("info", "No data to export.");
      return;
    }

    const headers = visibleColumns.map((c) => c.label);
    const rows = filteredData.map((row) =>
      visibleColumns.map((c) => {
        const val = getCellValue(row, c.key);
        if (val == null) return "";
        if (c.type === "boolean") return val ? "Yes" : "No";
        if (c.key === "top_location_id") {
          const tl = topLocations.find((t) => t.id === val);
          return tl?.name || row.top_location_name || val || "";
        }
        return String(val);
      })
    );

    setExportingSheets(true);
    try {
      await exportToGoogleSheets({
        title: `Master Locations (${new Date().toISOString().slice(0, 10)})`,
        headers,
        rows,
      });
    } finally {
      setExportingSheets(false);
    }
  };

  // Filtered rows by in-sheet filter text
  const filteredData = useMemo(() => {
    if (!activeFilterText.trim()) return data;
    const term = activeFilterText.toLowerCase();
    return data.filter((row) => {
      return (
        (row.location_name && row.location_name.toLowerCase().includes(term)) ||
        (row.store_code && row.store_code.toLowerCase().includes(term)) ||
        (row.city && row.city.toLowerCase().includes(term)) ||
        (row.street_address && row.street_address.toLowerCase().includes(term)) ||
        (row.internal_id && String(row.internal_id).toLowerCase().includes(term))
      );
    });
  }, [data, activeFilterText]);

  return (
    <div className="card radius-12 border-0 shadow-none bg-base">
      {/* ===== SPREADSHEET TOOLBAR ===== */}
      <div className="card-header bg-base border-bottom py-16 px-20 d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div className="d-flex align-items-center flex-wrap gap-3">
          {/* In-Sheet Quick Search */}
          <div className="position-relative" style={{ minWidth: "260px" }}>
            <Icon
              icon="solar:magnifer-linear"
              className="position-absolute top-50 start-0 translate-middle-y ms-12 text-secondary-light text-lg"
              style={{ pointerEvents: "none" }}
            />
            <input
              type="text"
              className="form-control form-control-sm ps-32 radius-8 bg-neutral-50"
              style={{ paddingLeft: "36px", paddingRight: activeFilterText ? "28px" : "12px" }}
              placeholder="Search in sheet..."
              value={activeFilterText}
              onChange={(e) => setActiveFilterText(e.target.value)}
            />
            {activeFilterText && (
              <button
                type="button"
                className="btn btn-sm btn-link position-absolute top-50 end-0 translate-middle-y p-0 me-8 text-secondary-light d-flex align-items-center"
                onClick={() => setActiveFilterText("")}
              >
                <Icon icon="solar:close-circle-bold" />
              </button>
            )}
          </div>

          {/* Pending Changes Indicator */}
          {pendingCount > 0 && (
            <div className="badge bg-warning-focus text-warning-main border border-warning-main px-12 py-8 radius-8 d-flex align-items-center gap-2">
              <Icon icon="solar:pen-new-square-bold" className="text-base" />
              <span className="fw-semibold">
                {pendingCount} change{pendingCount > 1 ? "s" : ""} pending ({editedRowsCount} row{editedRowsCount > 1 ? "s" : ""})
              </span>
            </div>
          )}
        </div>

        {/* Right Toolbar Actions */}
        <div className="d-flex align-items-center flex-wrap gap-2">
          {/* Discard Edits */}
          {pendingCount > 0 && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger radius-8 px-14 py-8 d-flex align-items-center gap-1 fw-semibold"
              onClick={handleDiscardAll}
              disabled={saving}
              title="Discard all pending changes"
            >
              <Icon icon="solar:restart-linear" className="text-base" />
              <span>Revert</span>
            </button>
          )}

          {/* Save All Changes Button */}
          <button
            type="button"
            className={`btn btn-sm radius-8 px-16 py-8 d-flex align-items-center gap-2 fw-bold shadow-sm ${
              pendingCount > 0 ? "btn-primary-600 animate__animated animate__pulse" : "btn-secondary text-white opacity-75"
            }`}
            onClick={handleSaveAll}
            disabled={saving || pendingCount === 0}
            title="Save all changes (Ctrl+S / Cmd+S)"
          >
            {saving ? (
              <Icon icon="line-md:loading-twotone-loop" className="text-lg" />
            ) : (
              <Icon icon="solar:diskette-bold" className="text-lg" />
            )}
            <span>Save All ({pendingCount})</span>
          </button>

          {/* Auto-Save Toggle */}
          <div className="form-check form-switch ms-2 d-flex align-items-center gap-2 mb-0" title="Automatically save cell edits on change">
            <input
              className="form-check-input cursor-pointer"
              type="checkbox"
              id="sheetAutoSaveSwitch"
              checked={autoSave}
              onChange={(e) => setAutoSave(e.target.checked)}
            />
            <label className="form-check-label text-xs fw-semibold text-secondary-light cursor-pointer" htmlFor="sheetAutoSaveSwitch">
              Auto-save
            </label>
          </div>

          <div className="vr mx-2 bg-neutral-200" style={{ height: "24px" }}></div>

          {/* Column Customizer Dropdown */}
          <div className="position-relative" ref={dropdownRef}>
            <button
              type="button"
              className="btn btn-sm btn-outline-neutral radius-8 px-14 py-8 d-flex align-items-center gap-2 fw-semibold bg-white text-secondary-light border hover-bg-neutral-50"
              onClick={() => setIsColumnDropdownOpen(!isColumnDropdownOpen)}
            >
              <Icon icon="solar:settings-minimalistic-linear" className="text-base text-primary-600" />
              <span>Columns</span>
              <span className="badge bg-primary-100 text-primary-600 radius-4 text-xs ms-1">
                {visibleColumns.length}/{ALL_COLUMNS.length}
              </span>
              <Icon icon={isColumnDropdownOpen ? "solar:alt-arrow-up-linear" : "solar:alt-arrow-down-linear"} className="text-xs text-secondary-light" />
            </button>

            {/* Dropdown Menu for Columns */}
            {isColumnDropdownOpen && (
              <div
                className="position-absolute end-0 mt-2 bg-white rounded-12 shadow-lg border p-16 z-3"
                style={{ width: "320px", maxHeight: "480px", overflowY: "auto" }}
              >
                <div className="d-flex align-items-center justify-content-between mb-12">
                  <h6 className="text-sm fw-bold mb-0">Customize Columns</h6>
                  <button
                    type="button"
                    className="btn btn-link p-0 text-xs text-primary-600 fw-semibold text-decoration-none"
                    onClick={handleResetColumns}
                  >
                    Reset Defaults
                  </button>
                </div>

                {/* Search within columns */}
                <div className="mb-12">
                  <input
                    type="text"
                    className="form-control form-control-sm radius-6 text-xs bg-neutral-50"
                    placeholder="Search columns..."
                    value={columnSearch}
                    onChange={(e) => setColumnSearch(e.target.value)}
                  />
                </div>

                {/* Quick select buttons */}
                <div className="d-flex align-items-center gap-2 mb-12 pb-8 border-bottom">
                  <button
                    type="button"
                    className="btn btn-xs btn-outline-primary radius-4 py-2 px-8 text-xs"
                    onClick={() => handleSelectAllColumns(true)}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="btn btn-xs btn-outline-secondary radius-4 py-2 px-8 text-xs"
                    onClick={() => handleSelectAllColumns(false)}
                  >
                    Deselect All
                  </button>
                </div>

                {/* Column Items with Reordering and Checkboxes */}
                <div className="d-flex flex-column gap-2">
                  {columnsOrder
                    .filter((item) => {
                      if (!columnSearch.trim()) return true;
                      const col = colMap.get(item.key);
                      return col && col.label.toLowerCase().includes(columnSearch.toLowerCase());
                    })
                    .map((item, index) => {
                      const col = colMap.get(item.key);
                      if (!col) return null;
                      return (
                        <div
                          key={item.key}
                          className="d-flex align-items-center justify-content-between p-6 radius-6 hover-bg-neutral-100 transition-all"
                        >
                          <label className="form-check d-flex align-items-center gap-2 mb-0 cursor-pointer flex-grow-1">
                            <input
                              type="checkbox"
                              className="form-check-input cursor-pointer"
                              checked={item.visible}
                              onChange={() => handleToggleColumn(item.key)}
                            />
                            <span className="text-xs fw-medium text-neutral-800">{col.label}</span>
                            <span className="badge bg-neutral-100 text-neutral-600 text-xxs px-6 py-2 radius-4 ms-auto me-8">
                              {col.category}
                            </span>
                          </label>

                          {/* Reorder Arrows */}
                          <div className="d-flex align-items-center gap-1">
                            <button
                              type="button"
                              className="btn btn-link p-0 text-secondary-light hover-text-primary-600"
                              disabled={index === 0}
                              onClick={(e) => handleMoveColumn(index, "up", e)}
                              title="Move Column Left / Up"
                            >
                              <Icon icon="solar:arrow-up-linear" className="text-sm" />
                            </button>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-secondary-light hover-text-primary-600"
                              disabled={index === columnsOrder.length - 1}
                              onClick={(e) => handleMoveColumn(index, "down", e)}
                              title="Move Column Right / Down"
                            >
                              <Icon icon="solar:arrow-down-linear" className="text-sm" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>

          {/* Export CSV */}
          <button
            type="button"
            className="btn btn-sm btn-outline-success radius-8 px-12 py-8 d-flex align-items-center gap-2 fw-semibold"
            onClick={handleExportCSV}
            title="Export current view to CSV"
          >
            <Icon icon="solar:file-download-linear" className="text-base" />
            <span>CSV</span>
          </button>

          {/* Export Google Sheets */}
          <button
            type="button"
            className="btn btn-sm btn-outline-success radius-8 px-12 py-8 d-flex align-items-center gap-2 fw-semibold"
            onClick={handleExportGoogleSheets}
            disabled={exportingSheets}
            title="Export current view to Google Sheets"
          >
            {exportingSheets ? (
              <Icon icon="line-md:loading-twotone-loop" className="text-base" />
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 13H16M8 17H16M11 9V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            <span>Google Sheets</span>
          </button>
        </div>
      </div>

      {/* ===== SPREADSHEET GRID CONTAINER ===== */}
      <div
        className="spreadsheet-grid-wrapper position-relative overflow-auto"
        style={{
          maxHeight: "calc(100vh - 280px)",
          minHeight: "450px",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-80">
            <Icon icon="line-md:loading-twotone-loop" className="text-primary-600 text-4xl" />
          </div>
        ) : filteredData.length === 0 ? (
          <div className="text-center py-60">
            <Icon icon="solar:notes-linear" className="text-4xl text-neutral-400 mb-12" />
            <p className="text-secondary-light fw-medium mb-0">No locations found matching the criteria.</p>
          </div>
        ) : (
          <table className="table table-bordered table-sm mb-0 align-middle spreadsheet-table">
            {/* Sticky Header Row */}
            <thead className="sticky-top bg-neutral-100 z-2 shadow-sm">
              <tr>
                {/* Index / Action Column */}
                <th
                  className="bg-neutral-200 text-center fw-bold text-xs text-secondary-dark px-10 py-10"
                  style={{ width: "60px", minWidth: "60px", position: "sticky", left: 0, zIndex: 3 }}
                >
                  #
                </th>

                {/* Quick Profile Link Column */}
                <th
                  className="bg-neutral-100 text-center fw-bold text-xs text-secondary-dark px-8 py-10"
                  style={{ width: "90px", minWidth: "90px", position: "sticky", left: "60px", zIndex: 3 }}
                >
                  Actions
                </th>

                {/* Visible Data Columns */}
                {visibleColumns.map((col) => (
                  <th
                    key={col.key}
                    className="bg-neutral-100 text-start fw-bold text-xs text-secondary-dark px-12 py-10 user-select-none"
                    style={{ minWidth: `${col.width}px`, width: `${col.width}px` }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <span className="text-truncate">{col.label}</span>
                      <span className="text-xxs text-secondary-light fw-normal ms-1">
                        {col.type === "boolean" ? "☑" : col.type === "number" ? "#" : ""}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {filteredData.map((row, rowIndex) => {
                const locId = row.location_id;
                const isRowEdited = !!edits[locId];

                return (
                  <tr
                    key={locId || rowIndex}
                    className={`spreadsheet-row ${isRowEdited ? "bg-warning-50" : ""}`}
                  >
                    {/* Sticky Row Index */}
                    <td
                      className="text-center text-xs fw-semibold text-secondary-light bg-neutral-50 px-8 py-6 user-select-none"
                      style={{ position: "sticky", left: 0, zIndex: 1 }}
                    >
                      {rowIndex + 1}
                    </td>

                    {/* Quick Row Actions (Edit, History, Compare) */}
                    <td
                      className="text-center px-6 py-4 bg-white"
                      style={{ position: "sticky", left: "60px", zIndex: 1 }}
                    >
                      <div className="d-flex align-items-center justify-content-center gap-1">
                        <Link
                          to={`/locations-main/${locId}/edit`}
                          className="w-24-px h-24-px bg-primary-50 text-primary-600 rounded d-flex align-items-center justify-content-center text-decoration-none hover-bg-primary-100"
                          title="Open Full Form"
                        >
                          <Icon icon="solar:pen-2-linear" className="text-xs" />
                        </Link>
                        {onOpenHistory && (
                          <button
                            type="button"
                            className="w-24-px h-24-px bg-info-50 text-info-main rounded d-flex align-items-center justify-content-center border-0 hover-bg-info-100"
                            onClick={() => onOpenHistory(locId)}
                            title="View History"
                          >
                            <Icon icon="solar:history-linear" className="text-xs" />
                          </button>
                        )}
                        {onOpenCompare && (
                          <button
                            type="button"
                            className="w-24-px h-24-px bg-success-50 text-success-main rounded d-flex align-items-center justify-content-center border-0 hover-bg-success-100"
                            onClick={() => onOpenCompare(locId)}
                            title="Compare Data"
                          >
                            <Icon icon="solar:scale-bold" className="text-xs" />
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Editable Cells */}
                    {visibleColumns.map((col) => {
                      const cellVal = getCellValue(row, col.key);
                      const isEdited = isCellEdited(locId, col.key);
                      const cellConflict =
                        col.key === "store_code" ? storeCodeConflicts[String(locId)] : null;

                      return (
                        <td
                          key={col.key}
                          className={`p-0 align-middle spreadsheet-cell ${
                            cellConflict
                              ? "bg-danger-50 border-danger"
                              : isEdited
                              ? "bg-warning-100 border-warning"
                              : ""
                          }`}
                          style={{ minWidth: `${col.width}px` }}
                        >
                          {/* Render appropriate inline editor based on column type */}
                          {col.type === "readonly" ? (
                            <div className="px-10 py-6 text-xs text-secondary-light fw-medium">
                              {col.key === "version_number" ? (
                                <span className="badge bg-neutral-200 text-neutral-800 radius-4">
                                  v{cellVal || 1}
                                </span>
                              ) : (
                                cellVal || "-"
                              )}
                            </div>
                          ) : col.type === "datetime" ? (
                            <div className="px-10 py-6 text-xs text-secondary-light">
                              {cellVal ? new Date(cellVal).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" }) : "-"}
                            </div>
                          ) : col.type === "boolean" ? (
                            <div className="d-flex align-items-center justify-content-center px-8 py-6">
                              <input
                                type="checkbox"
                                className="form-check-input cursor-pointer m-0"
                                checked={!!cellVal}
                                onChange={(e) => handleCellChange(locId, col.key, e.target.checked ? 1 : 0)}
                              />
                            </div>
                          ) : col.type === "select" && col.key === "top_location_id" ? (
                            <select
                              className="form-select form-select-sm border-0 bg-transparent text-xs radius-0 shadow-none px-8 py-6 w-100 cursor-pointer"
                              value={cellVal || ""}
                              onChange={(e) => handleCellChange(locId, col.key, e.target.value ? Number(e.target.value) : null)}
                            >
                              <option value="">-- None --</option>
                              {topLocations.map((tl) => (
                                <option key={tl.id} value={tl.id}>
                                  {tl.name}
                                </option>
                              ))}
                            </select>
                          ) : col.type === "number" ? (
                            <input
                              type="number"
                              step="any"
                              className="form-control form-control-sm border-0 bg-transparent text-xs radius-0 shadow-none px-10 py-6 w-100 font-monospace"
                              value={cellVal == null ? "" : cellVal}
                              onChange={(e) => handleCellChange(locId, col.key, e.target.value === "" ? null : Number(e.target.value))}
                              placeholder="-"
                            />
                          ) : (
                            <div>
                              <input
                                type="text"
                                className={`form-control form-control-sm border-0 bg-transparent text-xs radius-0 shadow-none px-10 py-6 w-100 ${
                                  cellConflict ? "text-danger-main fw-semibold" : ""
                                }`}
                                value={cellVal == null ? "" : cellVal}
                                onChange={(e) => handleCellChange(locId, col.key, e.target.value)}
                                placeholder="-"
                              />
                              {cellConflict && (
                                <div className="px-10 pb-6 d-flex flex-column gap-1 border-top border-danger-subtle bg-danger-50">
                                  <span className="text-danger-main fw-medium" style={{ fontSize: "10px", lineHeight: 1.2 }}>
                                    Already exists ({cellConflict.conflictingLocation?.location_name})
                                  </span>
                                  {cellConflict.suggestion && (
                                    <button
                                      type="button"
                                      className="btn btn-xs btn-outline-danger py-1 px-6 text-start d-inline-flex align-items-center gap-1 align-self-start"
                                      style={{ fontSize: "10px" }}
                                      onClick={() => handleCellChange(locId, "store_code", cellConflict.suggestion)}
                                    >
                                      <Icon icon="solar:magic-stick-3-bold" />
                                      Use: <strong>{cellConflict.suggestion}</strong>
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ===== SPREADSHEET FOOTER / STATUS BAR ===== */}
      <div className="card-footer bg-base border-top py-10 px-20 d-flex align-items-center justify-content-between text-xs text-secondary-light">
        <div className="d-flex align-items-center gap-3">
          <span>
            Showing <strong>{filteredData.length}</strong> of <strong>{data.length}</strong> location(s)
          </span>
          {activeFilterText && (
            <span className="badge bg-neutral-200 text-neutral-800 radius-4">
              Filtered: "{activeFilterText}"
            </span>
          )}
        </div>

        <div className="d-flex align-items-center gap-3">
          <span className="d-inline-flex align-items-center gap-1">
            <kbd className="bg-neutral-100 text-neutral-700 border px-6 py-2 radius-4 text-xxs">Ctrl+S</kbd> to save
          </span>
          <span>•</span>
          <span>{visibleColumns.length} active columns</span>
        </div>
      </div>
    </div>
  );
};

export default LocationSpreadsheetView;

